import { type JSX, type PropsWithChildren, useState, type ComponentProps } from 'react'
import { Link, router, Stack } from 'expo-router'
import { Section } from '@/src/common/components/Section'
import { mesaColorNames, typography } from '@/src/common/theme'
import { TouchableOpacity, View, Modal, StyleSheet } from 'react-native'
import { useEvent } from 'tamagui'
import * as CardActivity from '@/src/experiences/card-activity'

export function useCardManagementStackScreensAreModals() {
  return CardActivity.Components.Highnote.useIsCardActivityFeedBottomSheetFullHeight()
}

type StackScreenProps = ComponentProps<typeof Stack.Screen>

// eslint-disable-next-line @typescript-eslint/no-unsafe-function-type
type StackScreenOptions = NonNullable<Exclude<StackScreenProps['options'], Function>>

interface CardStackScreenProps extends Omit<StackScreenProps, 'options'> {
  options: Omit<StackScreenOptions, 'title'> & Required<Pick<StackScreenOptions, 'title'>>
  sectionProps?: Partial<ComponentProps<typeof Section>>
  backgroundColor?: string
  renderTitleInHeader?(): string
}

const minVerticalMargin = 48

export function useCardStackScreen(props: CardStackScreenProps) {
  const cardManagementStackScreensAreModals = useCardManagementStackScreensAreModals()
  const backgroundColor = props.backgroundColor ?? mesaColorNames['Mesa - Brand/Secondary #223843']

  const [modalVisible, setModalVisible] = useState(true)

  const dismiss = useEvent(async () => {
    setModalVisible(false)
    cardManagementStackScreensAreModals &&
      (await new Promise((resolve) => setTimeout(resolve, 222)))
    router.dismissTo('/main/card/card-management')
  })

  const cardStackScreenProps = {
    backgroundColor,
    ...props,
    options: {
      ...props.options,
      headerShown:
        typeof props.options.headerShown === 'boolean'
          ? props.options.headerShown
          : !cardManagementStackScreensAreModals,
      header() {
        return (
          <Section
            onClosePress={() => void dismiss()}
            style={{ backgroundColor }}
            headerStyle={{ height: 'auto', marginBottom: 0, padding: 24 }}
            titleStyles={[
              typography['heading/heading4'],
              { color: mesaColorNames['Mesa - Brand/White #FFFFFF'] },
            ]}
            chevronColor={mesaColorNames['Mesa - Brand/White #FFFFFF']}
            {...props.sectionProps}
            title={
              props.renderTitleInHeader?.() || props.sectionProps?.title || props.options.title
            }
          />
        )
      },
      contentStyle: [
        { backgroundColor: cardManagementStackScreensAreModals ? 'transparent' : backgroundColor },
        props.options.contentStyle,
      ],
    },
  } as const satisfies CardStackScreenProps

  const routerPush = useEvent((...args: Parameters<typeof router.push>) => {
    cardManagementStackScreensAreModals
      ? void dismiss().then(() => router.push(...args))
      : router.push(...args)
  })

  const [CardStackLink] = useState(() => (cardStackLinkProps: ComponentProps<typeof Link>) => (
    <Link
      push
      {...cardStackLinkProps}
      {...(cardManagementStackScreensAreModals
        ? {
            onPress(e) {
              e.preventDefault()
              cardStackLinkProps.onPress?.(e)
              routerPush(cardStackLinkProps.href)
            },
          }
        : undefined)}
    />
  ))

  return {
    ...cardStackScreenProps,

    modalVisible,
    setModalVisible,
    dismiss,

    Modal: (cardManagementStackScreensAreModals
      ? ({ children, contentContainerProps }) => (
          <Modal visible={modalVisible} transparent animationType="fade">
            <TouchableOpacity
              activeOpacity={1}
              onPress={() => void dismiss()}
              style={[StyleSheet.absoluteFill, { backgroundColor: '#00000050' }]}
            />
            <View
              style={{ width: 0, flexBasis: `17%`, flexShrink: 1, minHeight: minVerticalMargin }}
            />
            <View
              {...contentContainerProps}
              style={[
                {
                  margin: 'auto',
                  backgroundColor: cardStackScreenProps.backgroundColor,
                  borderRadius: 16,
                  overflow: 'hidden',
                  flexGrow: 0,
                  flexShrink: 1,
                  flexBasis: 'auto',
                  width: 484,
                  maxWidth: '88%',
                },
                contentContainerProps?.style,
              ]}
            >
              {
                // eslint-disable-next-line @typescript-eslint/no-unnecessary-boolean-literal-compare
                props.options.headerShown !== false && cardStackScreenProps.options.header()
              }
              {children}
            </View>
            <View
              style={{ width: 0, flexBasis: `17%`, flexGrow: 1, minHeight: minVerticalMargin }}
            />
          </Modal>
        )
      : ({ children }) => <>{children}</>) satisfies (
      props: PropsWithChildren<{
        contentContainerProps?: ComponentProps<typeof View>
      }>,
    ) => JSX.Element,

    screen() {
      return <Stack.Screen {...cardStackScreenProps} />
    },

    routerPush,

    CardStackLink,
  } as const
}

export function useCardStackScreenOld(props: CardStackScreenProps) {
  return useCardStackScreen(props).screen()
}
